import type { RasterLayerSource } from '@/hooks/useDatasets';
import { releaseTilesPath } from './releaseTiles';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import type { Color } from '@deck.gl/core';
import { GL } from '@luma.gl/constants';
import type { EncroachmentLayerConfig } from './types';
import { DEFAULT_TREE_COVER_MIN_ZOOM, LayerType } from './types';
import { getGeoBoundingBox } from '@/views/map/utils';

const RISK_OPACITY = 255;

const RISK_LEVEL_0: Color = [255, 240, 230, RISK_OPACITY * 0.35];
const RISK_LEVEL_1: Color = [255, 220, 205, RISK_OPACITY * 0.45];
const RISK_LEVEL_1_5: Color = [255, 195, 185, RISK_OPACITY * 0.55];
const RISK_LEVEL_2: Color = [255, 160, 150, RISK_OPACITY * 0.6];
const RISK_LEVEL_3: Color = [250, 120, 110, RISK_OPACITY * 0.7];
const RISK_LEVEL_3_5: Color = [240, 75, 75, RISK_OPACITY * 0.8];
const RISK_LEVEL_4: Color = [210, 20, 40, RISK_OPACITY * 0.9];

export const OVERHANG_LAYER_TYPE = 'overhang';

export const getEncroachmentLayer = (
  source: RasterLayerSource,
  config: EncroachmentLayerConfig,
  overhang: RasterLayerSource | null,
  isInspectMode: boolean,
  minZoom = DEFAULT_TREE_COVER_MIN_ZOOM,
  maxZoom = 18
) => {
  const ENCROACHMENT_OPACITY = 1;
  const encroachmentConfig = {
    ...config,
    visible: config.visible && !config.showOverhang,
  };
  const encroachmentLayers = [
    getRasterLayer(
      LayerType[LayerType.encroachment],
      source,
      encroachmentConfig,
      ENCROACHMENT_OPACITY,
      minZoom,
      maxZoom
    ),
  ];

  if (overhang) {
    const overhangConfig = {
      ...config,
      visible: config.visible && (isInspectMode || config.showOverhang),
      showOverhang: false, // Config for encroachment-layer when overhang is on. For overhang we don't want this special logic to trigger.
    };
    const overhangOpacity = isInspectMode && !config.showOverhang ? 0 : 1;
    encroachmentLayers.push(
      getRasterLayer(
        OVERHANG_LAYER_TYPE,
        overhang,
        overhangConfig,
        overhangOpacity,
        minZoom,
        maxZoom,
        true
      )
    );
  }

  return encroachmentLayers;
};

const getRasterLayer = (
  id: string,
  source: RasterLayerSource,
  config: EncroachmentLayerConfig,
  opacity: number,
  minZoom: number,
  maxZoom: number,
  isOverhang = false
) => {
  const cmap = {
    '0': RISK_LEVEL_0,
    '1': RISK_LEVEL_1,
    '5': RISK_LEVEL_1_5,
    '10': RISK_LEVEL_2,
    '100': RISK_LEVEL_3,
    '500': RISK_LEVEL_3_5,
    '1000': RISK_LEVEL_4,
  };

  // TODO: do not show risk value 0 for overhang for now.
  // Risk value 0 shows vegetation below the line which we don't want
  const overhangCmap = {
    '1': RISK_LEVEL_1,
    '5': RISK_LEVEL_1_5,
    '10': RISK_LEVEL_2,
    '100': RISK_LEVEL_3,
    '500': RISK_LEVEL_3_5,
    '1000': RISK_LEVEL_4,
  };

  const cmap_param = encodeURIComponent(
    JSON.stringify(isOverhang ? overhangCmap : cmap)
  );

  return new TileLayer({
    id,
    data: `${releaseTilesPath(source, 'png')}?pixel_selection=first&return_mask=true&bidx=1&resampling=nearest&colormap=${cmap_param}&tilesize=512`,
    loadOptions: {
      fetch: {
        credentials: 'include',
      },
    },
    minZoom,
    maxZoom,
    pickable: true,
    opacity,
    ...config,
    renderSubLayers: (props) => {
      if (props.data === null) {
        return null;
      }

      const boundingBox = getGeoBoundingBox(props);
      if (!boundingBox) {
        return null;
      }
      const { west, south, east, north } = boundingBox;

      return new BitmapLayer({
        id: props.id,
        image: props.data,
        bounds: [west, south, east, north],
        textureParameters: {
          [GL.TEXTURE_MIN_FILTER]: GL.NEAREST,
          [GL.TEXTURE_MAG_FILTER]: GL.NEAREST,
        },
        transparentColor: [255, 255, 255, 0],
        highlightColor: props.highlightColor,
        opacity: props.opacity,
        visible: props.visible,
        pickable: true,
      });
    },
  });
};
